import { Injectable, Inject } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { filter } from "rxjs/operators";
import { Store } from "@ngrx/store";

import { TodayService } from "@core/today.service";
import { DocumentsState } from "@app/user/documents/documents-store/documents.reducer";
import { DocumentsFilter } from "@app/user/documents/document-filter/document-filter";
import { resetDocumentFilter, resetDocuments } from "@app/user/documents/documents-store/documents.actions";
import { DocumentState, DocumentType } from "@helper/abstraction/documents";

@Injectable()
export class UserRoutingService {
	public isAfterCreate = false;
	public isAfterSign = false;
	public isAfterMobileSign = false;
	public steps = 0;
	private history: string[] = [];
	private isServiceWorking = false;

	constructor(
		private router: Router,
		private store: Store<DocumentsState>,
		private todayService: TodayService,
		@Inject("itemsToStore") public itemsToStore: number,
	) {
		if (!itemsToStore) {
			throw new Error("Please provide \"itemsToStore\" to UserRoutingService");
		}
	}

	public loadRouting(): void {
		if (this.isServiceWorking) {
			throw new Error("Routing service already working");
		}
		this.isServiceWorking = true;
		this.router.events
			.pipe(filter(event => event instanceof NavigationEnd))
			.subscribe((event: any) => {
				if (this.history[this.history.length - 1] === event.urlAfterRedirects.split("?")[0]) {
					this.history[this.history.length - 1] = event.urlAfterRedirects;
					return;
				}
				this.history = [...this.history.splice((this.itemsToStore * -1) + 1), event.urlAfterRedirects];
			});
	}

	public navigateBack(opts?: { force?: boolean; redirectTo?: string }, isProcurment = false, isMobileSigning = false): void {
		if (opts?.redirectTo && !isProcurment) {
			this.router.navigateByUrl(opts.redirectTo);
			return;
		}
		let backIndex = 0;
		const reversedHistory = this.history.reverse();
		reversedHistory.forEach((url, index) => {
			if (["all", "waiting_for_processing", "at_partner", "completed"].indexOf(url?.split("?")[1]?.split("&").reverse()[0]?.split("=")[1].toLocaleLowerCase()) !== -1) {
				if (!backIndex) {
					backIndex = index + 1;
				}
			}
		});

		if (isMobileSigning) {
			if (backIndex === 2) {
				const documentType = reversedHistory[1]?.split("?")[0]?.split("/").reverse()[0] as DocumentType;
				const documentState = reversedHistory[1]?.split("?")[1]?.split("&").reverse()[0]?.split("=")[1] as DocumentState;
				this.goListPage(documentType, documentState);
				return;
			}
			this.mobileNavigateBack(backIndex);
			return;
		}

		if (this.history.length <= 2 || opts?.force || backIndex === 0) {
			// eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
			isProcurment ? this.router.navigateByUrl(opts?.redirectTo!) : window.history.go(-1);
			return;
		}
		window.history.go(-backIndex);
	}

	public goListPage(documentType: DocumentType, documentState: DocumentState = "ALL"): void {
		const queryParams = { documentState };
		this.history = [];
		this.router.navigate(["user", "documents", documentType], { queryParams });
	}

	public goBackSkipPrevPage(documentType: DocumentType, isMobileSign = false): void {
		if (isMobileSign) {
			this.mobileNavigateBack(2);
			return;
		}
		window.history.go(-2);
	}

	public destroy(): void {
		this.isServiceWorking = false;
		this.history = [];
	}

	public resetDocsAndFilters(documentType: DocumentType, notMakeRequest?: boolean): void {
		const filter = new DocumentsFilter({
			documentState: "ALL",
			documentTypeId: documentType,
			processingStatusList: [],
			documentDateEnd: new Date(+this.todayService.oneMonth[1]),
			documentDateStart: new Date(+this.todayService.oneMonth[0]),
		});
		this.store.dispatch(resetDocuments());
		this.store.dispatch(resetDocumentFilter(filter.documentTypeId, filter, notMakeRequest));
	}

	private mobileNavigateBack(backIndex: number): void {
		if (this.steps > 0) {
			backIndex = backIndex + this.steps;
			this.steps = 0;
		}
		window.history.go(-backIndex);
	}
}
